import { useEffect } from 'react';
import { useProfile } from '../hooks/use-profile';
import { useUpdateProfile } from '../hooks/use-update-profile';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/atoms/avatar';
import { Skeleton } from '@/components/atoms/skeleton';
import { Separator } from '@/components/atoms/separator';
import { Badge } from '@/components/atoms/badge';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Camera,
  Mail,
  Key,
  MapPin,
  Calendar,
  Hash,
  CheckCircle2,
  Clock,
  XCircle,
  User,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { profileSchema, type ProfileFormData } from '@/lib/validations/settings.schema';
import { getInitials, extractDateOnly } from '@/lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export function ProfileContainer() {
  const { t } = useTranslation();
  const { data: profile, isLoading, error } = useProfile();
  const { mutate: updateProfile, isPending } = useUpdateProfile();

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: '',
      birthday: '',
    },
  });

  useEffect(() => {
    if (profile) {
      form.reset({
        name: profile.name ?? '',
        birthday: extractDateOnly(profile.birthday),
      });
    }
  }, [profile, form]);

  const handleSubmit = (data: ProfileFormData) => {
    updateProfile({
      name: data.name,
      birthday: data.birthday,
      address: data.address,
      image: data.image,
    });
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'active':
        return (
          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
            <CheckCircle2 className="size-3 mr-1" />
            {t('settings.profile.status.active')}
          </Badge>
        );
      case 'inactive':
        return (
          <Badge variant="secondary">
            <XCircle className="size-3 mr-1" />
            {t('settings.profile.status.inactive')}
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
            <Clock className="size-3 mr-1" />
            {t('settings.profile.status.pending')}
          </Badge>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Skeleton className="size-24 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('settings.profile.error')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Header Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {/* Avatar */}
            <div className="relative group">
              <Avatar className="size-24 border-4 border-background shadow-lg">
                <AvatarImage src={profile?.image} />
                <AvatarFallback className="text-2xl bg-gradient-to-br from-primary to-primary/60 text-primary-foreground">
                  {getInitials(profile?.name)}
                </AvatarFallback>
              </Avatar>
              <Button
                size="icon-sm"
                className="absolute -bottom-1 -right-1 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Camera className="size-3" />
              </Button>
            </div>

            {/* Profile Info */}
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold">{profile?.name}</h2>
                {getStatusBadge(profile?.status)}
              </div>
              <p className="text-muted-foreground flex items-center gap-2">
                <Mail className="size-4" />
                {profile?.email}
              </p>
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Hash className="size-3" />
                  {profile?.refId}
                </span>
                {profile?.birthday && (
                  <span className="flex items-center gap-1">
                    <Calendar className="size-3" />
                    {format(new Date(profile.birthday), 'dd MMMM yyyy', { locale: fr })}
                  </span>
                )}
                {profile?.created_at && (
                  <span className="flex items-center gap-1">
                    <User className="size-3" />
                    {t('settings.profile.memberSince')} {format(new Date(profile.created_at), 'MMMM yyyy', { locale: fr })}
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Address Card */}
      {profile?.address && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="size-5 text-red-500" />
              {t('settings.profile.address.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-4">
              <div className="size-12 rounded-lg bg-muted flex items-center justify-center text-2xl">
                {profile.address.country?.flagEmoji ?? '🌍'}
              </div>
              <div className="flex-1">
                <p className="font-medium">
                  {profile.address.address_line1}
                  {profile.address.address_line2 && `, ${profile.address.address_line2}`}
                </p>
                <p className="text-sm text-muted-foreground">
                  {profile.address.city}
                  {profile.address.postal_code && `, ${profile.address.postal_code}`}
                </p>
                <p className="text-sm text-muted-foreground">
                  {profile.address.country?.name}
                </p>
                {profile.address.landmark && (
                  <p className="text-sm text-muted-foreground mt-1">
                    <span className="text-foreground">{t('settings.profile.address.landmark')}:</span> {profile.address.landmark}
                  </p>
                )}
              </div>
              <Button variant="outline" size="sm">
                {t('settings.profile.address.edit')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profile Form */}
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.profile.personalInfo')}</CardTitle>
          <CardDescription>{t('settings.profile.personalInfoDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">{t('settings.profile.name')}</Label>
                <Input
                  id="name"
                  {...form.register('name')}
                />
                {form.formState.errors.name && (
                  <p className="text-sm text-destructive">
                    {t(form.formState.errors.name.message ?? '')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="birthday">{t('settings.profile.birthday')}</Label>
                <Input
                  id="birthday"
                  type="date"
                  {...form.register('birthday')}
                />
              </div>
            </div>
            <Button type="submit" disabled={isPending}>
              {isPending ? t('settings.profile.saving') : t('settings.profile.save')}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Separator />

      {/* Security Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.profile.security')}</CardTitle>
          <CardDescription>{t('settings.profile.securityDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-500/10 p-2">
                <Mail className="size-4 text-blue-500" />
              </div>
              <div>
                <p className="font-medium">{t('settings.profile.email')}</p>
                <p className="text-sm text-muted-foreground">{profile?.email}</p>
              </div>
            </div>
            <Link to="/settings/profile/email">
              <Button variant="outline" size="sm">
                {t('settings.profile.change')}
              </Button>
            </Link>
          </div>
          <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-orange-500/10 p-2">
                <Key className="size-4 text-orange-500" />
              </div>
              <div>
                <p className="font-medium">{t('settings.profile.password')}</p>
                <p className="text-sm text-muted-foreground">{t('settings.profile.passwordDescription')}</p>
              </div>
            </div>
            <Link to="/settings/profile/password">
              <Button variant="outline" size="sm">
                {t('settings.profile.change')}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
